import { useCallback } from 'react';
import { useParams } from 'react-router';

import { AlertColor, Stack } from '@mui/material';

import Page from '@/components/Page';
import Alert from '@/components/alert';
import Header from '@/components/header';
import Loading from '@/components/loading';
import { EvaluationResultsSteps } from '@/constants/driverSteps';
import { useFetchEvaluation } from '@/hooks/queries/evaluations';
import { useAppSelector } from '@/hooks/redux';
import { getPathWithLocalParams } from '@/hooks/urlParams';
import useDriver from '@/hooks/useDriver';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { getMonthsSince } from '@/utils/datesAndTime/datesAndTime';
import getRoute from '@/utils/getRoute';
import Error from '@/views/misc/error';

import EvaluationCriteria from './_partials/EvaluationCriteria';
import Body from './body/Body';

const EvaluationDetails = () => {
  const navigate = useWorkspaceNavigate();
  const evaluationId = Number(useParams().evaluationId);
  const user = useAppSelector((state) => state.user.currentUser);

  const { data, isLoading, error, isFetching } = useFetchEvaluation({
    id: evaluationId
  });

  useDriver({
    isLoading: isFetching,
    steps: EvaluationResultsSteps
  });

  const getTermsAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
    action: string;
  } => {
    const startCreationDate = new Date(data.parameters.startCreationDate);
    const endCreationDate = data.parameters.endCreationDate
      ? new Date(data.parameters.endCreationDate)
      : null;
    const generalTerm = new Date(user.workspaceStorageTerms.general);
    const violationTerm = new Date(user.workspaceStorageTerms.violation);

    const deletionTerms = `General term: ${getMonthsSince(
      generalTerm
    )} months. Violation term: ${getMonthsSince(violationTerm)} months.`;

    if (endCreationDate !== null && endCreationDate < violationTerm) {
      return {
        severity: 'error',
        action: deletionTerms,
        message:
          'All posts in this evaluation have expired according to the data storage terms.'
      };
    }

    if (startCreationDate < generalTerm) {
      return {
        severity: 'warning',
        action: deletionTerms,
        message:
          'This evaluation may contain expired posts, since its start date falls outside of the data storage term.'
      };
    }

    return { severity: undefined, action: '', message: '' };
  }, [data, user]);

  if (error)
    return (
      <Page>
        <Error message="Failed to get evaluation details" error={error} />
      </Page>
    );

  if (isLoading) return <Loading />;

  const termsAlert = getTermsAlert();

  return (
    <Page
      title="Evaluation Details"
      header={
        <Header
          isSmallHeader
          breadcrumbsList={[
            {
              name: 'Evaluations',
              onClick: () =>
                navigate(getPathWithLocalParams(getRoute.evaluations.LIST()))
            }
          ]}
          onClickReturn={() =>
            navigate(getPathWithLocalParams(getRoute.evaluations.LIST()))
          }
          title={data?.title}
        />
      }
    >
      <Stack gap={2.5}>
        <EvaluationCriteria isFetching={isFetching} />
        {termsAlert.message && (
          <Alert
            sx={{ alignItems: 'center' }}
            severity={termsAlert.severity}
            content={termsAlert.message}
            action={termsAlert.action}
          />
        )}
        <Body />
      </Stack>
    </Page>
  );
};

export default EvaluationDetails;
